import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { AudioSampleRecorder } from './AudioSampleRecorder';
import {
  RemoteProcedureCall,
  useApplicationStateLoader,
  useApplicationStateValue,
} from '@resistdesign/voltra/app/utils';
import { PROFILE } from '../Profile/IDs';
import { APP_ENV_CONFIG } from '../../config';
import { uploadFileToS3SignedUrl } from '../../Utils/FileUtils';
import {
  CenterContent,
  CenterContentControls,
  CenteredScreenLayout,
  EmptyCenterLeft,
  EmptyCenterRight,
  EmptyFooter,
  EmptyHeader,
  StructuredText,
} from '../../Components/CenteredScreenLayout';
import { Countdown } from './Countdown';

const {
  api: { domain: API_DOMAIN },
} = APP_ENV_CONFIG;

const RECORDING_TIME = 60000;

export type AudioRecordingScreenProps = {
  authorization: string;
};

export const AudioRecordingScreen: FC<AudioRecordingScreenProps> = ({ authorization }) => {
  const [listening, setListening] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [audioSample, setAudioSample] = useState<Blob | null>(null);
  const [audioUploadLoading, setAudioUploadLoading] = useState(false);
  const UPLOAD_AUDIO_SAMPLE_URL_RPC: RemoteProcedureCall = useMemo(
    () => ({
      serviceConfig: {
        protocol: 'https',
        domain: API_DOMAIN,
        basePath: '/user/file-urls',
        authorization,
      },
      path: '/upload-audio-sample',
    }),
    [authorization]
  );
  const { loading: audioSampleUrlLoading, makeRemoteProcedureCall: getAudioSampleUploadUrl } =
    useApplicationStateLoader({
      identifier: PROFILE.AUDIO_SAMPLE_UPLOAD_URL,
      remoteProcedureCall: UPLOAD_AUDIO_SAMPLE_URL_RPC,
      manual: true,
    });
  const { value: audioSampleUrl } = useApplicationStateValue(PROFILE.AUDIO_SAMPLE_UPLOAD_URL);
  const onSampleRecorded = useCallback(
    (sample: Blob) => {
      if (cancelled) {
        setAudioSample(null);
      } else {
        setAudioSample(sample);
        getAudioSampleUploadUrl();
      }
    },
    [cancelled, setAudioSample, getAudioSampleUploadUrl]
  );
  const onRecordAudio = useCallback(() => {
    setCancelled(false);
    setAudioSample(null);
    setListening(true);
  }, []);
  const onCompleteAudioRecording = useCallback(() => {
    setListening(false);
  }, []);
  const onCancelAudioRecording = useCallback(() => {
    setCancelled(true);
    setListening(false);
  }, []);
  const onGoToInterviewComplete = useCallback(async () => {
    history.pushState({}, '', '/interview/complete');
  }, []);
  const onUploadAudioSample = useCallback(async () => {
    if (audioSample && !!audioSampleUrl) {
      setAudioUploadLoading(true);
      await uploadFileToS3SignedUrl(
        audioSampleUrl,
        // Convert the Blob into a File.
        new File(
          [audioSample],
          // A simple and consistent file name.
          'AUDIO_SAMPLE.wav',
          { type: audioSample.type }
        )
      );
      setAudioUploadLoading(false);
      onGoToInterviewComplete();
    }
  }, [audioSample, audioSampleUrl, onGoToInterviewComplete]);

  useEffect(() => {
    if (listening) {
      const timer = setTimeout(() => {
        setListening(false);
      }, RECORDING_TIME);

      return () => clearTimeout(timer);
    }
  }, [listening]);

  return (
    <CenteredScreenLayout>
      <EmptyHeader />
      <EmptyCenterLeft />
      <CenterContent>
        <AudioSampleRecorder onSampleRecorded={onSampleRecorded} recording={listening} />
        <strong>Audio Recording:</strong>
        <br />
        <br />
        {audioUploadLoading ? (
          <>
            <progress />
          </>
        ) : (
          <>
            <StructuredText>
              Record 1 minute of a verbal response answering the question:
              <br />
              <strong>Why do you wish to explore the advisor opportunity?</strong>
            </StructuredText>
            <br />
            <CenterContentControls>
              <button
                aria-busy={listening ? true : undefined}
                aria-label={listening ? 'Please wait…' : ''}
                onClick={onRecordAudio}
                disabled={listening}
              >
                {listening ? (
                  <>
                    [<Countdown seconds={RECORDING_TIME / 1000} />
                    ]&nbsp;
                  </>
                ) : undefined}
                {audioSample ? 'Retry' : listening ? 'Recording...' : 'Record'}
              </button>
              {listening ? <button onClick={onCompleteAudioRecording}>Done</button> : undefined}
              {listening ? <button onClick={onCancelAudioRecording}>Cancel</button> : undefined}
              {audioSample && audioSampleUrl ? <button onClick={onUploadAudioSample}>Complete</button> : undefined}
            </CenterContentControls>
          </>
        )}
      </CenterContent>
      <EmptyCenterRight />
      <EmptyFooter />
    </CenteredScreenLayout>
  );
};
